import type { MembershipApplicationStepId } from '@/features/membership/application/types'
import type { RequirementDto } from '@/utils/api/membership-service/models'
import { RequirementType, type Requirement } from './types'

export interface RequirementValidationPayload {
  description?: string
  section?: MembershipApplicationStepId
  acceptedFormats?: string[]
  placeholder?: string
  helperText?: string
  options?: Array<{ label: string; value: string }>
  min?: number
  max?: number
  [key: string]: unknown
}

const requirementTypeValues = new Set<RequirementType>(Object.values(RequirementType))

export function normaliseRequirementType(rawType: unknown): RequirementType {
  if (typeof rawType === 'string') {
    const upper = rawType.toUpperCase() as keyof typeof RequirementType
    if (upper in RequirementType) {
      return RequirementType[upper]
    }
    if (requirementTypeValues.has(rawType as RequirementType)) {
      return rawType as RequirementType
    }
  } else if (rawType && typeof rawType === 'object' && 'toString' in rawType) {
    const stringified = String(rawType)
    const upper = stringified.toUpperCase() as keyof typeof RequirementType
    if (upper in RequirementType) {
      return RequirementType[upper]
    }
    if (requirementTypeValues.has(stringified as RequirementType)) {
      return stringified as RequirementType
    }
  }

  return RequirementType.TEXT
}

export function parseRequirementValidation(
  validationJson?: string,
): RequirementValidationPayload | undefined {
  if (!validationJson) return undefined

  try {
    const parsed = JSON.parse(validationJson) as RequirementValidationPayload
    return parsed
  } catch (error) {
    console.warn('Failed to parse requirement validation payload:', error)
    return undefined
  }
}

export interface ResolvedRequirement {
  id?: number
  label: string
  mandatory: boolean
  type: RequirementType
  description?: string
  metadata?: RequirementValidationPayload
}

const SECTION_KEYWORDS: Array<{
  stepId: MembershipApplicationStepId
  keywords: string[]
}> = [
  {
    stepId: 'employment',
    keywords: ['employment', 'employer', 'experience', 'job', 'role', 'position', 'work history'],
  },
  {
    stepId: 'education',
    keywords: ['education', 'qualification', 'degree', 'certificate', 'course', 'diploma', 'training'],
  },
  {
    stepId: 'affiliations',
    keywords: ['affiliation', 'association', 'membership', 'award', 'recognition', 'honor'],
  },
]

function matchesKeyword(content: string, keywords: string[]) {
  return keywords.some((keyword) => content.includes(keyword))
}

export function normaliseRequirement(
  requirement?: Requirement | (RequirementDto & { createdAt?: string; updatedAt?: string }),
): ResolvedRequirement | undefined {
  if (!requirement || typeof requirement !== 'object') {
    return undefined
  }

  const label = typeof requirement.label === 'string' ? requirement.label.trim() : ''
  if (!label) {
    return undefined
  }

  const metadata = parseRequirementValidation(requirement.validationJson)
  const description = typeof metadata?.description === 'string' ? metadata.description : undefined

  return {
    id: requirement.id,
    label,
    mandatory: typeof requirement.mandatory === 'boolean' ? requirement.mandatory : false,
    type: normaliseRequirementType((requirement as { type?: unknown }).type),
    description,
    metadata,
  }
}

export function inferRequirementStep(
  requirement: ResolvedRequirement,
  fallback: MembershipApplicationStepId = 'profile',
): MembershipApplicationStepId {
  const section = requirement.metadata?.section
  if (section && typeof section === 'string') {
    const normalised = section.toLowerCase() as MembershipApplicationStepId
    if (
      normalised === 'profile'
      || normalised === 'employment'
      || normalised === 'education'
      || normalised === 'affiliations'
      || normalised === 'documents'
      || normalised === 'review'
    ) {
      return normalised
    }
  }

  if (requirement.type === RequirementType.FILE) {
    return 'documents'
  }

  const haystack = [requirement.label, requirement.description]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  for (const { stepId, keywords } of SECTION_KEYWORDS) {
    if (haystack && matchesKeyword(haystack, keywords)) {
      return stepId
    }
  }

  return fallback
}

export function isDocumentRequirement(requirement: ResolvedRequirement) {
  return requirement.type === RequirementType.FILE
}
